import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Image as ImageIcon, Film, Plus, Save, RotateCcw, Trash2, ChevronUp, ChevronDown,
    Wand2, Eye, EyeOff, Star, ExternalLink, AlertCircle, Loader2, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import {
    fetchAdminPortfolio,
    createBrandingItem, updateBrandingItem, deleteBrandingItem,
    createVideoItem, updateVideoItem, deleteVideoItem,
    resetPortfolio, lookupBehance,
} from "@/lib/adminApi";

const SPAN_OPTIONS = [
    { value: "lg:col-span-4", label: "1/3 width" },
    { value: "lg:col-span-5", label: "40% width" },
    { value: "lg:col-span-6", label: "1/2 width" },
    { value: "lg:col-span-7", label: "60% width" },
    { value: "lg:col-span-8", label: "2/3 width" },
    { value: "lg:col-span-12", label: "Full width" },
];

export default function AdminPortfolio() {
    const [activeTab, setActiveTab] = useState("branding"); // branding | video
    const [branding, setBranding] = useState([]);
    const [video, setVideo] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await fetchAdminPortfolio();
            setBranding(Array.isArray(data.branding) ? data.branding : []);
            setVideo(Array.isArray(data.video) ? data.video : []);
        } catch (_e) {
            toast.error("Could not load portfolio");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const onResetDefaults = async () => {
        if (!window.confirm("Reset portfolio to defaults? This restores all originals and clears custom items.")) return;
        setBusy(true);
        try {
            const data = await resetPortfolio();
            setBranding(data.branding || []);
            setVideo(data.video || []);
            toast.success("Portfolio reset to defaults");
        } catch (_e) {
            toast.error("Reset failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="px-5 py-8 sm:px-8 sm:py-10 lg:px-10">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <span className="pg-eyebrow inline-flex items-center gap-2">
                        <Sparkles className="h-3.5 w-3.5" /> Studio CMS · Portfolio Manager
                    </span>
                    <h1
                        className="font-display mt-4 text-3xl sm:text-4xl"
                        style={{ color: "var(--pg-fg)" }}
                        data-testid="admin-portfolio-headline"
                    >
                        Portfolio &amp; Reels
                    </h1>
                    <p className="mt-2 max-w-2xl text-sm" style={{ color: "var(--pg-fg-muted)" }}>
                        Paste a Behance / YouTube / Vimeo link and we&apos;ll auto-fetch the title, description and thumbnail.
                        Manual override fields are always available below.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        onClick={onResetDefaults}
                        disabled={busy || loading}
                        data-testid="admin-portfolio-reset"
                        className="pg-btn-ghost pg-app-tap disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        <RotateCcw className="h-4 w-4" /> Reset Defaults
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className="mt-6 inline-flex gap-1 rounded-full border p-1" style={{ borderColor: "var(--pg-border)" }}>
                {[
                    { id: "branding", label: "Branding", icon: ImageIcon, count: branding.length },
                    { id: "video", label: "Video / Motion", icon: Film, count: video.length },
                ].map((t) => {
                    const active = activeTab === t.id;
                    return (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => setActiveTab(t.id)}
                            data-testid={`admin-portfolio-tab-${t.id}`}
                            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-mono uppercase tracking-[0.16em] transition-colors"
                            style={{
                                background: active ? "var(--pg-fg)" : "transparent",
                                color: active ? "var(--pg-bg)" : "var(--pg-fg-muted)",
                            }}
                        >
                            <t.icon className="h-3.5 w-3.5" />
                            {t.label}
                            <span className="ml-1 rounded-full px-1.5 text-[10px]" style={{
                                background: active ? "color-mix(in srgb, var(--pg-bg) 25%, transparent)" : "var(--pg-surface)",
                                color: active ? "var(--pg-bg)" : "var(--pg-fg-muted)",
                            }}>{t.count}</span>
                        </button>
                    );
                })}
            </div>

            <div className="mt-6">
                {loading ? (
                    <div
                        className="flex items-center justify-center rounded-2xl border px-6 py-20 text-sm"
                        style={{ borderColor: "var(--pg-border)", color: "var(--pg-fg-muted)" }}
                    >
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading portfolio…
                    </div>
                ) : activeTab === "branding" ? (
                    <BrandingManager items={branding} setItems={setBranding} reload={load} />
                ) : (
                    <VideoManager items={video} setItems={setVideo} reload={load} />
                )}
            </div>
        </div>
    );
}

// ====================== Branding manager ======================

const emptyBranding = () => ({
    id: null, // null = new
    title: "",
    category: "Brand Identity",
    description: "",
    image: "",
    behance: "",
    span: "lg:col-span-6",
    featured: false,
    visible: true,
    order: 0,
});

const BrandingManager = ({ items, setItems, reload }) => {
    const [editing, setEditing] = useState(null); // item id or null
    const [draft, setDraft] = useState(null);     // unsaved item form
    const sorted = useMemo(() => [...items].sort((a, b) => (a.order || 0) - (b.order || 0)), [items]);

    const openNew = () => {
        setDraft({ ...emptyBranding(), order: sorted.length });
        setEditing(null);
    };
    const openEdit = (item) => {
        setDraft({ ...emptyBranding(), ...item });
        setEditing(item.id);
    };
    const closeForm = () => { setDraft(null); setEditing(null); };

    const onDelete = async (id) => {
        if (!window.confirm("Delete this portfolio item?")) return;
        try {
            await deleteBrandingItem(id);
            setItems((arr) => arr.filter((x) => x.id !== id));
            toast.success("Deleted");
            if (editing === id) closeForm();
        } catch (_e) {
            toast.error("Could not delete");
        }
    };

    const onToggleVisible = async (item) => {
        try {
            const updated = await updateBrandingItem(item.id, { visible: !item.visible });
            setItems((arr) => arr.map((x) => (x.id === item.id ? updated : x)));
            toast.success(updated.visible ? "Now visible" : "Hidden");
        } catch (_e) {
            toast.error("Could not update");
        }
    };

    const onToggleFeatured = async (item) => {
        try {
            const updated = await updateBrandingItem(item.id, { featured: !item.featured });
            setItems((arr) => arr.map((x) => (x.id === item.id ? updated : x)));
        } catch (_e) {
            toast.error("Could not update");
        }
    };

    const onSubmit = async (draftData) => {
        try {
            if (editing) {
                const updated = await updateBrandingItem(editing, draftData);
                setItems((arr) => arr.map((x) => (x.id === editing ? updated : x)));
                toast.success("Saved");
            } else {
                const created = await createBrandingItem(draftData);
                setItems((arr) => [...arr, created]);
                toast.success("Added to portfolio");
            }
            closeForm();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error("Could not save", { description: typeof detail === "string" ? detail : "Please retry." });
        }
    };

    return (
        <div className="space-y-5">
            <div className="flex justify-end">
                <button
                    type="button"
                    onClick={openNew}
                    className="pg-btn-primary pg-app-tap"
                    data-testid="admin-portfolio-branding-add"
                >
                    <Plus className="h-3.5 w-3.5" /> Add Branding Project
                </button>
            </div>

            <AnimatePresence>
                {draft && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                    >
                        <BrandingEditor
                            draft={draft}
                            isNew={!editing}
                            onSubmit={onSubmit}
                            onCancel={closeForm}
                        />
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Grid of cards */}
            <ul className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="admin-portfolio-branding-list">
                {sorted.map((item) => (
                    <PortfolioRow
                        key={item.id}
                        kind="branding"
                        item={item}
                        onEdit={() => openEdit(item)}
                        onDelete={() => onDelete(item.id)}
                        onToggleVisible={() => onToggleVisible(item)}
                        onToggleFeatured={() => onToggleFeatured(item)}
                    />
                ))}
            </ul>
        </div>
    );
};

const BrandingEditor = ({ draft, isNew, onSubmit, onCancel }) => {
    const [data, setData] = useState(draft);
    const [lookupUrl, setLookupUrl] = useState(draft.behance || "");
    const [busy, setBusy] = useState(false);

    useEffect(() => { setData(draft); setLookupUrl(draft.behance || ""); }, [draft]);

    const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

    const runLookup = async () => {
        if (!lookupUrl.trim()) {
            toast.error("Paste a URL first");
            return;
        }
        setBusy(true);
        try {
            const meta = await lookupBehance(lookupUrl.trim());
            const next = { ...data, behance: lookupUrl.trim() };
            if (meta.title && !next.title) next.title = meta.title;
            if (meta.description && !next.description) next.description = meta.description;
            if (meta.image && !next.image) next.image = meta.image;
            setData(next);
            toast.success("Auto-filled from URL", { description: meta.site_name || "" });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error("Could not fetch metadata", {
                description: typeof detail === "string" ? detail : "Use the manual fields below.",
            });
        } finally {
            setBusy(false);
        }
    };

    const submit = (e) => {
        e.preventDefault();
        if (!data.title.trim()) { toast.error("Title is required"); return; }
        onSubmit(data);
    };

    return (
        <form
            onSubmit={submit}
            className="pg-app-surface p-5 sm:p-7"
            data-testid="admin-portfolio-branding-editor"
        >
            <h2 className="font-display text-xl sm:text-2xl" style={{ color: "var(--pg-fg)" }}>
                {isNew ? "New Branding Project" : "Edit Project"}
            </h2>

            {/* Auto-detect strip */}
            <div className="mt-5 rounded-2xl border p-4" style={{ borderColor: "var(--pg-border)", background: "var(--pg-surface)" }}>
                <div className="flex items-center gap-2">
                    <Wand2 className="h-3.5 w-3.5" style={{ color: "var(--pg-accent)" }} />
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "var(--pg-fg-muted)" }}>
                        Auto-detect (Behance · YouTube · Vimeo · most sites)
                    </span>
                </div>
                <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                    <input
                        type="url"
                        value={lookupUrl}
                        onChange={(e) => setLookupUrl(e.target.value)}
                        placeholder="https://www.behance.net/gallery/..."
                        className="pg-input flex-1"
                        data-testid="admin-portfolio-branding-lookup-url"
                    />
                    <button
                        type="button"
                        onClick={runLookup}
                        disabled={busy}
                        className="pg-btn-ghost pg-app-tap shrink-0 disabled:cursor-not-allowed disabled:opacity-60"
                        data-testid="admin-portfolio-branding-lookup-btn"
                    >
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
                        {busy ? "Fetching…" : "Auto-fill"}
                    </button>
                </div>
                <p className="mt-2 text-[11px]" style={{ color: "var(--pg-fg-muted)" }}>
                    Won&apos;t overwrite fields you&apos;ve already filled. Manual override fields are always available below.
                </p>
            </div>

            {/* Manual override fields */}
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <FormField label="Title *" testId="admin-portfolio-branding-title">
                    <input type="text" required value={data.title} onChange={(e) => set("title", e.target.value)} className="pg-input" placeholder="e.g. Aurelia Atelier" />
                </FormField>
                <FormField label="Category" testId="admin-portfolio-branding-category">
                    <input type="text" value={data.category} onChange={(e) => set("category", e.target.value)} className="pg-input" placeholder="Brand Identity" />
                </FormField>
            </div>

            <FormField label="Description" testId="admin-portfolio-branding-description" className="mt-4">
                <textarea rows={3} value={data.description} onChange={(e) => set("description", e.target.value)} className="pg-input" style={{ resize: "vertical", minHeight: 80 }} />
            </FormField>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <FormField label="Image URL" testId="admin-portfolio-branding-image">
                    <input type="url" value={data.image} onChange={(e) => set("image", e.target.value)} className="pg-input" placeholder="https://…" />
                </FormField>
                <FormField label="Behance / Project URL" testId="admin-portfolio-branding-behance">
                    <input type="url" value={data.behance} onChange={(e) => set("behance", e.target.value)} className="pg-input" placeholder="https://www.behance.net/..." />
                </FormField>
            </div>

            <div className="mt-4 grid gap-4 sm:grid-cols-[1fr_auto_auto]">
                <FormField label="Grid Width" testId="admin-portfolio-branding-span">
                    <select value={data.span} onChange={(e) => set("span", e.target.value)} className="pg-input">
                        {SPAN_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </FormField>
                <FormField label="Featured" testId="admin-portfolio-branding-featured">
                    <label className="inline-flex h-10 items-center gap-2 px-2">
                        <input type="checkbox" checked={!!data.featured} onChange={(e) => set("featured", e.target.checked)} />
                        <Star className="h-3.5 w-3.5" style={{ color: data.featured ? "var(--pg-accent)" : "var(--pg-fg-muted)" }} />
                    </label>
                </FormField>
                <FormField label="Visible" testId="admin-portfolio-branding-visible">
                    <label className="inline-flex h-10 items-center gap-2 px-2">
                        <input type="checkbox" checked={data.visible !== false} onChange={(e) => set("visible", e.target.checked)} />
                        {data.visible !== false ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                    </label>
                </FormField>
            </div>

            {/* Image preview */}
            {data.image && (
                <div className="mt-5">
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "var(--pg-fg-muted)" }}>
                        Preview
                    </span>
                    <div className="mt-1.5 overflow-hidden rounded-2xl border" style={{ borderColor: "var(--pg-border)", aspectRatio: "16/10" }}>
                        <img src={data.image} alt="preview" className="h-full w-full object-cover" onError={(e) => { e.target.style.opacity = 0.2; }} />
                    </div>
                </div>
            )}

            <div className="mt-6 flex flex-wrap items-center gap-2">
                <button type="submit" className="pg-btn-primary pg-app-tap" data-testid="admin-portfolio-branding-save">
                    <Save className="h-4 w-4" /> {isNew ? "Add Project" : "Save Changes"}
                </button>
                <button type="button" onClick={onCancel} className="pg-btn-ghost pg-app-tap" data-testid="admin-portfolio-branding-cancel">
                    Cancel
                </button>
            </div>
        </form>
    );
};

// ====================== Video manager ======================

const emptyVideo = () => ({
    id: null,
    title: "",
    category: "Brand Film",
    description: "",
    poster: "",
    watch: "",
    duration: "",
    span: "lg:col-span-6",
    featured: false,
    visible: true,
    order: 0,
});

const VideoManager = ({ items, setItems, reload }) => {
    const [editing, setEditing] = useState(null);
    const [draft, setDraft] = useState(null);
    const sorted = useMemo(() => [...items].sort((a, b) => (a.order || 0) - (b.order || 0)), [items]);

    const openNew = () => { setDraft({ ...emptyVideo(), order: sorted.length }); setEditing(null); };
    const openEdit = (item) => { setDraft({ ...emptyVideo(), ...item }); setEditing(item.id); };
    const closeForm = () => { setDraft(null); setEditing(null); };

    const onDelete = async (id) => {
        if (!window.confirm("Delete this video?")) return;
        try {
            await deleteVideoItem(id);
            setItems((arr) => arr.filter((x) => x.id !== id));
            toast.success("Deleted");
            if (editing === id) closeForm();
        } catch (_e) { toast.error("Could not delete"); }
    };

    const onToggleVisible = async (item) => {
        try {
            const updated = await updateVideoItem(item.id, { visible: !item.visible });
            setItems((arr) => arr.map((x) => (x.id === item.id ? updated : x)));
            toast.success(updated.visible ? "Now visible" : "Hidden");
        } catch (_e) { toast.error("Could not update"); }
    };

    const onToggleFeatured = async (item) => {
        try {
            const updated = await updateVideoItem(item.id, { featured: !item.featured });
            setItems((arr) => arr.map((x) => (x.id === item.id ? updated : x)));
        } catch (_e) { toast.error("Could not update"); }
    };

    const onSubmit = async (draftData) => {
        try {
            if (editing) {
                const updated = await updateVideoItem(editing, draftData);
                setItems((arr) => arr.map((x) => (x.id === editing ? updated : x)));
                toast.success("Saved");
            } else {
                const created = await createVideoItem(draftData);
                setItems((arr) => [...arr, created]);
                toast.success("Added to reel");
            }
            closeForm();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error("Could not save", { description: typeof detail === "string" ? detail : "Please retry." });
        }
    };

    return (
        <div className="space-y-5">
            <div className="flex justify-end">
                <button
                    type="button"
                    onClick={openNew}
                    className="pg-btn-primary pg-app-tap"
                    data-testid="admin-portfolio-video-add"
                >
                    <Plus className="h-3.5 w-3.5" /> Add Video / Motion
                </button>
            </div>

            <AnimatePresence>
                {draft && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                    >
                        <VideoEditor draft={draft} isNew={!editing} onSubmit={onSubmit} onCancel={closeForm} />
                    </motion.div>
                )}
            </AnimatePresence>

            <ul className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="admin-portfolio-video-list">
                {sorted.map((item) => (
                    <PortfolioRow
                        key={item.id}
                        kind="video"
                        item={item}
                        onEdit={() => openEdit(item)}
                        onDelete={() => onDelete(item.id)}
                        onToggleVisible={() => onToggleVisible(item)}
                        onToggleFeatured={() => onToggleFeatured(item)}
                    />
                ))}
            </ul>
        </div>
    );
};

const VideoEditor = ({ draft, isNew, onSubmit, onCancel }) => {
    const [data, setData] = useState(draft);
    const [lookupUrl, setLookupUrl] = useState(draft.watch || "");
    const [busy, setBusy] = useState(false);

    useEffect(() => { setData(draft); setLookupUrl(draft.watch || ""); }, [draft]);

    const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

    const runLookup = async () => {
        if (!lookupUrl.trim()) { toast.error("Paste a URL first"); return; }
        setBusy(true);
        try {
            const meta = await lookupBehance(lookupUrl.trim());
            const next = { ...data, watch: lookupUrl.trim() };
            if (meta.title && !next.title) next.title = meta.title;
            if (meta.description && !next.description) next.description = meta.description;
            if (meta.image && !next.poster) next.poster = meta.image;
            setData(next);
            toast.success("Auto-filled from URL", { description: meta.site_name || "" });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error("Could not fetch metadata", {
                description: typeof detail === "string" ? detail : "Use the manual fields below.",
            });
        } finally { setBusy(false); }
    };

    const submit = (e) => {
        e.preventDefault();
        if (!data.title.trim()) { toast.error("Title is required"); return; }
        onSubmit(data);
    };

    return (
        <form
            onSubmit={submit}
            className="pg-app-surface p-5 sm:p-7"
            data-testid="admin-portfolio-video-editor"
        >
            <h2 className="font-display text-xl sm:text-2xl" style={{ color: "var(--pg-fg)" }}>
                {isNew ? "New Video / Motion Project" : "Edit Video"}
            </h2>

            {/* Auto-detect */}
            <div className="mt-5 rounded-2xl border p-4" style={{ borderColor: "var(--pg-border)", background: "var(--pg-surface)" }}>
                <div className="flex items-center gap-2">
                    <Wand2 className="h-3.5 w-3.5" style={{ color: "var(--pg-accent)" }} />
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "var(--pg-fg-muted)" }}>
                        Auto-detect (YouTube · Vimeo · Behance · most sites)
                    </span>
                </div>
                <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                    <input
                        type="url"
                        value={lookupUrl}
                        onChange={(e) => setLookupUrl(e.target.value)}
                        placeholder="https://www.youtube.com/watch?v=..."
                        className="pg-input flex-1"
                        data-testid="admin-portfolio-video-lookup-url"
                    />
                    <button
                        type="button"
                        onClick={runLookup}
                        disabled={busy}
                        className="pg-btn-ghost pg-app-tap shrink-0 disabled:cursor-not-allowed disabled:opacity-60"
                        data-testid="admin-portfolio-video-lookup-btn"
                    >
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
                        {busy ? "Fetching…" : "Auto-fill"}
                    </button>
                </div>
            </div>

            <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <FormField label="Title *" testId="admin-portfolio-video-title">
                    <input type="text" required value={data.title} onChange={(e) => set("title", e.target.value)} className="pg-input" />
                </FormField>
                <FormField label="Category" testId="admin-portfolio-video-category">
                    <input type="text" value={data.category} onChange={(e) => set("category", e.target.value)} className="pg-input" placeholder="Brand Film" />
                </FormField>
            </div>

            <FormField label="Description" testId="admin-portfolio-video-description" className="mt-4">
                <textarea rows={3} value={data.description} onChange={(e) => set("description", e.target.value)} className="pg-input" style={{ resize: "vertical", minHeight: 80 }} />
            </FormField>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <FormField label="Poster Image URL" testId="admin-portfolio-video-poster">
                    <input type="url" value={data.poster} onChange={(e) => set("poster", e.target.value)} className="pg-input" placeholder="https://…" />
                </FormField>
                <FormField label="Watch URL (YouTube / Vimeo)" testId="admin-portfolio-video-watch">
                    <input type="url" value={data.watch} onChange={(e) => set("watch", e.target.value)} className="pg-input" placeholder="https://www.youtube.com/..." />
                </FormField>
            </div>

            <div className="mt-4 grid gap-4 sm:grid-cols-[1fr_1fr_auto_auto]">
                <FormField label="Duration" testId="admin-portfolio-video-duration">
                    <input type="text" value={data.duration} onChange={(e) => set("duration", e.target.value)} className="pg-input" placeholder="0:48" />
                </FormField>
                <FormField label="Grid Width" testId="admin-portfolio-video-span">
                    <select value={data.span} onChange={(e) => set("span", e.target.value)} className="pg-input">
                        {SPAN_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </FormField>
                <FormField label="Featured" testId="admin-portfolio-video-featured">
                    <label className="inline-flex h-10 items-center gap-2 px-2">
                        <input type="checkbox" checked={!!data.featured} onChange={(e) => set("featured", e.target.checked)} />
                        <Star className="h-3.5 w-3.5" style={{ color: data.featured ? "var(--pg-accent)" : "var(--pg-fg-muted)" }} />
                    </label>
                </FormField>
                <FormField label="Visible" testId="admin-portfolio-video-visible">
                    <label className="inline-flex h-10 items-center gap-2 px-2">
                        <input type="checkbox" checked={data.visible !== false} onChange={(e) => set("visible", e.target.checked)} />
                        {data.visible !== false ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                    </label>
                </FormField>
            </div>

            {data.poster && (
                <div className="mt-5">
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "var(--pg-fg-muted)" }}>Preview</span>
                    <div className="mt-1.5 overflow-hidden rounded-2xl border" style={{ borderColor: "var(--pg-border)", aspectRatio: "16/10" }}>
                        <img src={data.poster} alt="preview" className="h-full w-full object-cover" onError={(e) => { e.target.style.opacity = 0.2; }} />
                    </div>
                </div>
            )}

            <div className="mt-6 flex flex-wrap items-center gap-2">
                <button type="submit" className="pg-btn-primary pg-app-tap" data-testid="admin-portfolio-video-save">
                    <Save className="h-4 w-4" /> {isNew ? "Add Video" : "Save Changes"}
                </button>
                <button type="button" onClick={onCancel} className="pg-btn-ghost pg-app-tap" data-testid="admin-portfolio-video-cancel">
                    Cancel
                </button>
            </div>
        </form>
    );
};

// ====================== Shared row ======================

const PortfolioRow = ({ kind, item, onEdit, onDelete, onToggleVisible, onToggleFeatured }) => {
    const img = kind === "branding" ? item.image : item.poster;
    const link = kind === "branding" ? item.behance : item.watch;
    const hidden = item.visible === false;
    return (
        <li
            className="group relative overflow-hidden rounded-2xl border transition-all"
            style={{
                borderColor: "var(--pg-border)",
                background: "var(--pg-surface)",
                opacity: hidden ? 0.55 : 1,
            }}
            data-testid={`admin-portfolio-${kind}-row`}
            data-item-id={item.id}
        >
            <div className="relative aspect-[16/10] overflow-hidden" style={{ background: "var(--pg-surface-2)" }}>
                {img ? (
                    <img src={img} alt={item.title} className="h-full w-full object-cover" onError={(e) => { e.target.style.opacity = 0.15; }} />
                ) : (
                    <div className="flex h-full w-full items-center justify-center">
                        {kind === "branding" ? <ImageIcon className="h-8 w-8 opacity-30" /> : <Film className="h-8 w-8 opacity-30" />}
                    </div>
                )}
                <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
                    {item.featured && (
                        <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.16em]" style={{
                            borderColor: "rgba(255,255,255,0.3)",
                            background: "rgba(0,0,0,0.55)",
                            color: "#FFF",
                        }}>
                            <Star className="h-2.5 w-2.5" /> Featured
                        </span>
                    )}
                    {hidden && (
                        <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.16em]" style={{
                            borderColor: "rgba(255,255,255,0.3)",
                            background: "rgba(0,0,0,0.55)",
                            color: "#FFF",
                        }}>
                            <EyeOff className="h-2.5 w-2.5" /> Hidden
                        </span>
                    )}
                </div>
            </div>
            <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <h3 className="font-display text-base sm:text-lg" style={{ color: "var(--pg-fg)" }}>{item.title || "Untitled"}</h3>
                        <p className="mt-0.5 text-[12px] font-mono uppercase tracking-[0.18em]" style={{ color: "var(--pg-fg-muted)" }}>
                            {item.category || "—"}{kind === "video" && item.duration ? ` · ${item.duration}` : ""}
                        </p>
                    </div>
                    {link && (
                        <a href={link} target="_blank" rel="noopener noreferrer" className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border" style={{ borderColor: "var(--pg-border)", color: "var(--pg-fg-muted)" }} aria-label="Open original">
                            <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                    )}
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button type="button" onClick={onEdit} className="pg-btn-ghost pg-app-tap" data-testid={`admin-portfolio-${kind}-edit-${item.id}`}>Edit</button>
                    <button type="button" onClick={onToggleVisible} className="pg-btn-ghost pg-app-tap" data-testid={`admin-portfolio-${kind}-visible-${item.id}`}>
                        {hidden ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                        {hidden ? "Show" : "Hide"}
                    </button>
                    <button type="button" onClick={onToggleFeatured} className="pg-btn-ghost pg-app-tap" data-testid={`admin-portfolio-${kind}-feature-${item.id}`}>
                        <Star className="h-3.5 w-3.5" style={{ color: item.featured ? "var(--pg-accent)" : "inherit" }} />
                        {item.featured ? "Unfeature" : "Feature"}
                    </button>
                    <button
                        type="button"
                        onClick={onDelete}
                        className="ml-auto inline-flex h-8 items-center gap-1.5 rounded-md border px-3 text-xs transition-colors hover:bg-[var(--pg-surface-2)]"
                        style={{ borderColor: "var(--pg-border)", color: "#E55353" }}
                        data-testid={`admin-portfolio-${kind}-delete-${item.id}`}
                    >
                        <Trash2 className="h-3.5 w-3.5" /> Delete
                    </button>
                </div>
            </div>
        </li>
    );
};

const FormField = ({ label, children, testId, className }) => (
    <div className={`flex flex-col gap-1.5 ${className || ""}`} data-testid={testId}>
        <span className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "var(--pg-fg-muted)" }}>{label}</span>
        {children}
    </div>
);
